import React, { useEffect, useState } from 'react';
import CustomiseSection from '../../../../medicalHistoryConfigPanel/CustomiseSections';
import { useDispatch, useSelector } from 'react-redux';
import { MEDICAL_HISTORY_CATEGORY, MHItem } from '../../../../../store/types/medicalHistory';
import { Switch, notification } from 'antd';
import {
    upsertGenieSetting,
    upsertMedicalHistoryConfig,
} from '../../../../../store/actions/session';
import {
    GenieSettings,
    MedicalHistoryConfig,
    MedicalHistorySectionSettingsItem,
} from '../../../../../api/pushGenie';
import {
    DEFAULT_MEDICAL_HISTORY_CONFIG_VALUES,
    MEDICAL_HISTORY_SECTIONS_OPTION,
} from '../../../../../constants';
import { track } from '../../../../../tracking';
import { MISC_KEYS, getItemFromMisc } from '../../../../../store/idbHandler';
import { getMyIdWithBackwardCompatibility } from '../../../../../api/mactiveid';
import { GlobalState } from '../../../../../store/reducers';

const NewPatientMHConfig: React.FC<{
    closeMe: () => void;
    onRefresh: () => void;
}> = ({ closeMe, onRefresh }) => {
    const { commonItems, sections } = DEFAULT_MEDICAL_HISTORY_CONFIG_VALUES;
    const [medicalHistoryConfigData, setMedicalHistoryConfigData] = useState<MedicalHistoryConfig>(
        DEFAULT_MEDICAL_HISTORY_CONFIG_VALUES,
    );

    const [showMedicalHistoryInAddPt, setShowMedicalHistoryInAddPt] = useState(true);

    const [medicalHistorySectionConfig, setMedicalHistorySectionConfig] = useState<
        MedicalHistorySectionSettingsItem[]
    >(sections.map((i) => ({ ...i, isShown: true })));

    const [medicalHistoryCommonItems, setMedicalHistoryCommonItems] = useState<MHItem[]>(
        commonItems,
    );

    const dispatch = useDispatch();

    const rxDocId = useSelector(
        (state: GlobalState) => state.prescriptionPad.prescription?.doctor?._id,
    );

    const myId = getMyIdWithBackwardCompatibility() || '';

    const getMedicalHistoryConfigData = () => {
        getItemFromMisc<MedicalHistoryConfig>(myId, MISC_KEYS.medicalHistoryConfig)
            .then((medicalHistoryConfigData) => {
                setMedicalHistoryConfigData(medicalHistoryConfigData);
            })
            .catch(console.error);
    };

    const getShowMedicalHistoryInAddPt = () => {
        getItemFromMisc<GenieSettings>(myId, MISC_KEYS.settings)
            .then((settings) => {
                setShowMedicalHistoryInAddPt(settings.showMedicalHistoryInAddPt);
            })
            .catch(console.error);
    };

    useEffect(() => {
        getMedicalHistoryConfigData();
        getShowMedicalHistoryInAddPt();
    }, []);

    useEffect(() => {
        const sectionItemsKeysSet = new Set(medicalHistoryConfigData?.sections?.map((r) => r.key));
        const mergedSectionAndDefaultConfig = [
            ...new Map(
                [
                    ...(medicalHistoryConfigData?.sections || []),
                    ...MEDICAL_HISTORY_SECTIONS_OPTION,
                ].map((item) => [item['key'], item]),
            ).values(),
        ];

        setMedicalHistorySectionConfig(
            mergedSectionAndDefaultConfig.map((i) => {
                return {
                    ...i,
                    isShown: sectionItemsKeysSet.has(i.key),
                };
            }),
        );

        setMedicalHistoryCommonItems([...(medicalHistoryConfigData?.commonItems || [])]);
    }, [medicalHistoryConfigData]);

    const setMedicalHistoryCommonItemsSectionData = (
        catId: MEDICAL_HISTORY_CATEGORY,
        option: MHItem,
    ) => {
        const items = {
            catId: catId,
            id: option.id,
            name: option.name,
        };

        setMedicalHistoryCommonItems([...medicalHistoryCommonItems, items]);
    };

    function deleteMedicalHistoryCommonItemsElement(id: string) {
        const data = medicalHistoryCommonItems.filter((e) => e.id !== id);
        setMedicalHistoryCommonItems(data);
    }

    function toggleShowMHinAddPt() {
        track('TOGGLE_MH_IN_ADD_PATIENT', { mhVisible: showMedicalHistoryInAddPt.toString() });
        setShowMedicalHistoryInAddPt((val) => !val);
    }

    function onSave() {
        track('ADD_PATIENT_CONFIGURE', { from: 'Medical History Tab' });

        const mhConfig = {
            commonItems: medicalHistoryCommonItems,
            sections: medicalHistorySectionConfig,
        };

        const myId = getMyIdWithBackwardCompatibility() || '';
        dispatch(upsertMedicalHistoryConfig(mhConfig, rxDocId || myId));

        dispatch(
            upsertGenieSetting({
                showMedicalHistoryInAddPt: showMedicalHistoryInAddPt as boolean,
            }),
        );

        getMedicalHistoryConfigData();

        notification.success({ message: 'Patient History Configuration updated', duration: 5 });
        closeMe && closeMe();
        setTimeout(onRefresh, 100);
    }

    return (
        <div className="flex flex-col gap-4 pt-16 h-full">
            <div className="flex items-center self-end gap-8 px-16">
                <p className="font-700 text-13 text-darwin-neutral_new-1000">
                    {showMedicalHistoryInAddPt ? 'Disable' : 'Enable'} medical history
                </p>
                <Switch checked={showMedicalHistoryInAddPt} onChange={toggleShowMHinAddPt} />
            </div>
            <CustomiseSection
                setMedicalHistoryCommonItemsSectionData={setMedicalHistoryCommonItemsSectionData}
                medicalHistoryCommonItems={medicalHistoryCommonItems}
                deleteMedicalHistoryCommonItemsElement={deleteMedicalHistoryCommonItemsElement}
                disabled={!showMedicalHistoryInAddPt}
            />
            <div className="flex mt-auto items-center justify-between px-16 py-8 space-x-8 border-t border-neutral-7">
                <div>
                    <button onClick={closeMe} className="neutral-btn btn--small px-16 py-8">
                        <span className="w-full text-center">Cancel</span>
                    </button>
                </div>
                <div>
                    <button
                        onClick={onSave}
                        className="primary-btn  px-12 py-8 btn btn--small space-x-8"
                    >
                        <span className="w-full text-center">Save</span>
                    </button>
                </div>
            </div>
        </div>
    );
};

export default NewPatientMHConfig;
